import React, { useState, useEffect } from "react";
import { useDialer } from "../hooks/useDialer";
import "./Dialer.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

const DIALPAD = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];

export default function Dialer() {
  const [number, setNumber] = useState("");
  const [view, setView] = useState("dialpad"); // dialpad | queues | calls
  const [queues, setQueues] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [agentName] = useState(() => `agent_${Math.floor(Math.random() * 1000)}`);

  const {
    status, isReady, incomingCall, isMuted, isOnHold,
    formattedDuration, error, dial, hangup, accept, reject,
    toggleMute, toggleHold, sendDTMF,
  } = useDialer(agentName);

  const isInCall = status === "in-call" || status === "connecting" || status === "ringing";

  // ── Fetch queue + call stats ───────────────────────────────────────────────
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [qRes, cRes] = await Promise.all([
          fetch(`${BACKEND}/queues`),
          fetch(`${BACKEND}/calls`),
        ]);
        setQueues(await qRes.json());
        setActiveCalls(await cRes.json());
      } catch {}
    };
    fetchStats();
    const interval = setInterval(fetchStats, 8000);
    return () => clearInterval(interval);
  }, []);

  const handleKey = (digit) => {
    if (isInCall) { sendDTMF(digit); return; }
    setNumber(n => n + digit);
  };

  const handleBackspace = () => setNumber(n => n.slice(0, -1));

  const handleDial = () => {
    if (!number.trim()) return;
    dial(number.trim());
  };

  const statusLabel = {
    idle: isReady ? "Ready" : "Connecting...",
    connecting: "Dialing...",
    ringing: "Ringing...",
    "in-call": formattedDuration,
    error: "Error",
  }[status] || "—";

  const statusColor = {
    idle: isReady ? "#22c55e" : "#f59e0b",
    connecting: "#f59e0b",
    ringing: "#3b82f6",
    "in-call": "#22c55e",
    error: "#ef4444",
  }[status] || "#6b7280";

  return (
    <div className="dialer-shell">
      {/* Incoming call banner */}
      {incomingCall && (
        <div className="incoming-banner">
          <div className="incoming-info">
            <span className="incoming-icon">📞</span>
            <div>
              <div className="incoming-label">Incoming Call</div>
              <div className="incoming-from">{incomingCall.parameters?.From || "Unknown"}</div>
            </div>
          </div>
          <div className="incoming-actions">
            <button className="btn-accept" onClick={accept}>Accept</button>
            <button className="btn-decline" onClick={reject}>Decline</button>
          </div>
        </div>
      )}

      <div className="dialer-card">
        {/* Header */}
        <div className="dialer-header">
          <div className="brand">
            <span className="brand-dot" style={{ background: statusColor }} />
            <span className="brand-name">Leovoid Dialer</span>
          </div>
          <div className="agent-badge">{agentName}</div>
        </div>

        {/* Nav */}
        <div className="dialer-nav">
          {["dialpad", "queues", "calls"].map(v => (
            <button
              key={v}
              className={`nav-btn ${view === v ? "active" : ""}`}
              onClick={() => setView(v)}
            >
              {v === "dialpad" ? "⌨️ Dial" : v === "queues" ? "📋 Queues" : "📞 Calls"}
            </button>
          ))}
        </div>

        {/* Status bar */}
        <div className="status-bar">
          <span className="status-dot" style={{ background: statusColor }} />
          <span className="status-text" style={{ color: statusColor }}>{statusLabel}</span>
          {error && <span className="status-error">{error}</span>}
        </div>

        {/* ── DIALPAD VIEW ───────────────────────────────────────────────── */}
        {view === "dialpad" && (
          <>
            {/* Number display */}
            <div className="number-display">
              <input
                className="number-input"
                value={number}
                onChange={e => setNumber(e.target.value)}
                placeholder="+1 (555) 000-0000"
                disabled={isInCall}
              />
              {number && !isInCall && (
                <button className="backspace-btn" onClick={handleBackspace}>⌫</button>
              )}
            </div>

            {/* Dialpad grid */}
            <div className="dialpad-grid">
              {DIALPAD.map(([digit, letters]) => (
                <button
                  key={digit}
                  className="dialpad-key"
                  onClick={() => handleKey(digit)}
                >
                  <span className="key-digit">{digit}</span>
                  {letters && <span className="key-letters">{letters}</span>}
                </button>
              ))}
            </div>

            {/* Call controls */}
            <div className="call-controls">
              {!isInCall ? (
                <button
                  className="btn-call"
                  onClick={handleDial}
                  disabled={!isReady || !number.trim()}
                >
                  <span>📞</span> Call
                </button>
              ) : (
                <>
                  <button
                    className={`btn-control ${isMuted ? "active" : ""}`}
                    onClick={toggleMute}
                    title="Mute"
                  >
                    {isMuted ? "🔇" : "🎙️"}
                  </button>
                  <button
                    className="btn-hangup"
                    onClick={hangup}
                  >
                    <span>📵</span> End
                  </button>
                  <button
                    className={`btn-control ${isOnHold ? "active" : ""}`}
                    onClick={toggleHold}
                    title="Hold"
                  >
                    {isOnHold ? "▶️" : "⏸️"}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {/* ── QUEUES VIEW ────────────────────────────────────────────────── */}
        {view === "queues" && (
          <div className="info-view">
            <div className="info-title">Call Queues</div>
            {queues.length === 0 ? (
              <div className="empty-state">No queues found.<br />Create queues in Twilio Console.</div>
            ) : (
              <div className="queue-list">
                {queues.map(q => (
                  <div className="queue-item" key={q.sid}>
                    <div className="queue-name">{q.friendlyName}</div>
                    <div className="queue-stats">
                      <span className="stat">
                        <span className="stat-val">{q.currentSize}</span>
                        <span className="stat-lbl">waiting</span>
                      </span>
                      <span className="stat">
                        <span className="stat-val">{q.averageWaitTime || 0}s</span>
                        <span className="stat-lbl">avg wait</span>
                      </span>
                    </div>
                    <div className={`queue-status ${q.currentSize > 0 ? "busy" : "clear"}`}>
                      {q.currentSize > 0 ? "● Active" : "○ Clear"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── ACTIVE CALLS VIEW ──────────────────────────────────────────── */}
        {view === "calls" && (
          <div className="info-view">
            <div className="info-title">Active Calls</div>
            {activeCalls.length === 0 ? (
              <div className="empty-state">No active calls right now.</div>
            ) : (
              <div className="calls-list">
                {activeCalls.map(c => (
                  <div className="call-item" key={c.sid}>
                    <div className="call-direction">
                      {c.direction === "inbound" ? "↙" : "↗"}
                    </div>
                    <div className="call-info">
                      <div className="call-number">{c.direction === "inbound" ? c.from : c.to}</div>
                      <div className="call-meta">{c.status} · {c.direction}</div>
                    </div>
                    <div className="call-status-dot" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
